import {
  BALANCE,
  BUILDINGS,
  BUILDING_IDS,
  ECONOMY,
  FACTIONS,
  TECHS,
  TECH_IDS,
  TRADE_GOOD_IDS,
  type BuildingId,
  type SlotLayer,
  type TradeGoodId,
} from '@sd/content';
import { hasKey, t, type Locale } from '@sd/i18n';
import {
  buildBlocker,
  buildCost,
  canAfford,
  slotCount,
  slotsUsed,
  techMods,
  toLocal,
  upgradeBlocker,
  type Command,
  type GameState,
  type PlayerState,
} from '@sd/sim';
import type { Viewer } from '../map/describe';
import { clock, num, shortOf } from './format';

/**
 * The «Planet» tab (#57): the capital's numbers, its buildings with their
 * upgrades, what can be built next, and last cycle's goods. Pure: state in,
 * words, numbers and ready commands out; the panel only draws them.
 */
export interface Offer {
  /** Cash in the player's own currency, and goods. */
  readonly cash: number;
  readonly alloys: number;
  readonly metals: number;
  readonly cycles: number;
  /** Ready to send, or null with the reason it cannot go. */
  readonly command: Command | null;
  readonly reason: string | null;
  /** The simulation's own reason code, for sorting. */
  readonly code: string | null;
}

export interface BuildingRow {
  readonly index: number;
  readonly id: BuildingId;
  readonly name: string;
  readonly layer: SlotLayer;
  readonly level: number;
  readonly maxLevel: number;
  /** The level under construction and the time it still takes. */
  readonly building: {
    readonly level: number;
    readonly left: string;
    /** Share built, 0..1: the tile fills with it (#164). */
    readonly value: number;
  } | null;
  readonly does: string;
  readonly upgrade: Offer | null;
}

export interface BuildOption {
  readonly id: BuildingId;
  readonly layer: SlotLayer;
  readonly name: string;
  readonly does: string;
  readonly offer: Offer;
  /** Behind a technology the player has not learnt. */
  readonly locked: boolean;
}

export interface Stat {
  readonly label: string;
  readonly value: string;
  readonly note: string;
  readonly alert: boolean;
}

export interface PlanetView {
  readonly title: string;
  readonly subtitle: string;
  readonly stats: readonly Stat[];
  readonly slots: Readonly<Record<SlotLayer, { readonly used: number; readonly total: number }>>;
  readonly buildings: readonly BuildingRow[];
  readonly options: readonly BuildOption[];
  readonly cycle: readonly {
    readonly good: TradeGoodId;
    readonly produced: number;
    readonly consumed: number;
    readonly net: number;
  }[];
}

/**
 * Why the simulation would refuse, in the player's words: `<scope>.reason.*`
 * first when the tab words it its own way, then the shared `reason.*`, else
 * the simulation's code as it is.
 */
export function reasonText(locale: Locale, reason: string, tech?: string, scope?: string): string {
  const id = reason.replace(/\s+(\w)/g, (_, c: string) => c.toUpperCase());
  const params = { tech: tech ? t(locale, `tech.${tech}.name`) : '' };
  if (scope && hasKey(`${scope}.reason.${id}`)) return t(locale, `${scope}.reason.${id}`, params);
  return hasKey(`reason.${id}`) ? t(locale, `reason.${id}`, params) : reason;
}

/** What a building does per level, from its numbers in content. */
export function buildingDoes(locale: Locale, id: BuildingId, player: PlayerState): string {
  const def = BUILDINGS[id];
  const good = (g: TradeGoodId) => t(locale, `resource.${g}`).toLowerCase();
  const parts: string[] = [];
  const ins = Object.entries(def.inputs) as [TradeGoodId, number][];
  const outs = Object.entries(def.outputs) as [TradeGoodId, number][];
  if (ins.length && outs.length) {
    parts.push(
      `${ins.map(([g, n]) => `${num(locale, n, n % 1 ? 1 : 0)} ${good(g)}`).join(' + ')} → ${outs
        .map(([g, n]) => `${num(locale, n)} ${good(g)}`)
        .join(' + ')}`,
    );
  } else {
    for (const [g, n] of outs) parts.push(`+${num(locale, n)} ${good(g)}`);
  }
  if (def.extracts) {
    parts.push(`+${num(locale, def.extracts)} ${good(player.planet.deposit)}`);
  }
  if (def.science) parts.push(t(locale, 'planet.does.science', { n: def.science }));
  if (def.energyOutput) parts.push(t(locale, 'planet.does.energy', { n: def.energyOutput }));
  if (def.populationCap) parts.push(t(locale, 'planet.does.housing', { n: def.populationCap }));
  // The district's levels are the capital's new surface slots (#79).
  if (id === 'housing') {
    const [, l2, l3] = ECONOMY.surfaceSlots.byHousingLevel;
    parts.push(t(locale, 'planet.does.slots', { l2: l2 ?? 0, l3: (l3 ?? 0) - (l2 ?? 0) }));
  }
  if (def.garrison) parts.push(t(locale, 'planet.does.garrison', { n: def.garrison }));
  if (def.shield) parts.push(t(locale, 'planet.does.shield', { n: def.shield }));
  if (def.guns) parts.push(t(locale, 'planet.does.guns', { n: def.guns }));
  if (!parts.length) parts.push(t(locale, `planet.does.${id}`));
  const upkeep = [
    def.jobs ? t(locale, 'planet.does.jobs', { n: def.jobs }) : '',
    def.energy ? t(locale, 'planet.does.uses', { n: def.energy }) : '',
  ].filter(Boolean);
  return `${parts.join(', ')}${upkeep.length ? ` · ${upkeep.join(', ')}` : ''}`;
}

/**
 * How to get a slot on `layer` (#150: «нет свободной ячейки» said nothing of it): the
 * housing district's next level, then technologies not yet learnt that add one.
 */
export function slotRemedy(
  locale: Locale,
  player: PlayerState,
  layer: SlotLayer,
  /** Only where to get one (#156: the disabled tile in place of a slot). */
  short = false,
): string {
  const ways: string[] = [];
  if (layer === 'surface') {
    const housing = Math.max(
      0,
      ...player.planet.buildings.filter((b) => b.id === 'housing').map((b) => b.level),
    );
    const steps = ECONOMY.surfaceSlots.byHousingLevel;
    const next = steps.findIndex((n, i) => i + 1 > housing && n > (steps[housing - 1] ?? 0));
    if (next >= 0) {
      ways.push(
        t(locale, 'remedy.housing', {
          level: next + 1,
          n: steps[next]! - (steps[housing - 1] ?? 0),
        }),
      );
    }
  }
  const techs = TECH_IDS.filter(
    (id) =>
      !player.techs.includes(id) &&
      ((layer === 'surface' ? TECHS[id].effects.surfaceSlots : TECHS[id].effects.orbitSlots) ?? 0) >
        0,
  ).map((id) => `«${t(locale, `tech.${id}.name`)}»`);
  if (techs.length) ways.push(t(locale, 'remedy.techs', { list: techs.join(', ') }));
  if (short) {
    return ways.length
      ? t(locale, 'remedy.slotFrom', { ways: ways.join('; ') })
      : t(locale, 'remedy.slotMax');
  }
  return ways.length
    ? t(locale, `remedy.noSlot.${layer}`, { ways: ways.join('; ') })
    : t(locale, `remedy.noSlotMax.${layer}`);
}

function offer(
  locale: Locale,
  player: PlayerState,
  id: BuildingId,
  level: number,
  command: Command,
  blocker: string | null,
  layer: SlotLayer = BUILDINGS[id].layers[0]!,
): Offer {
  const gs = buildCost(id, level);
  const cost = { ...gs, cash: toLocal(gs.cash, player.currency.rate) };
  const short = blocker ?? canAfford(cost, player.cash, player.goods);
  // Each blocker says how to clear it (#150): what is missing and how much, where a slot
  // comes from.
  const reason = !short
    ? null
    : short === 'no free slot'
      ? slotRemedy(locale, player, layer)
      : /not enough/.test(short)
        ? (shortOf(locale, player, cost.cash, cost.alloys, cost.metals) ??
          reasonText(locale, short))
        : reasonText(locale, short, BUILDINGS[id].requiresTech ?? undefined);
  return {
    cash: cost.cash,
    alloys: cost.alloys,
    metals: cost.metals,
    cycles: BUILDINGS[id].buildCycles,
    command: short ? null : command,
    reason,
    code: short,
  };
}

export function planetView(state: GameState, v: Viewer): PlanetView | null {
  const { locale, me } = v;
  const player = state.players.find((p) => p.id === me);
  if (!player) return null;
  const planet = player.planet;
  const mods = techMods(player.techs);
  const c = planet.lastCycle;
  const home = state.system.planets.find((p) => p.homeOf === me);
  const orbit = home ? home.orbit + 1 : 0;

  const stats: Stat[] = (
    [
      {
        label: t(locale, 'planet.population'),
        value: num(locale, planet.population, 1),
        note: t(locale, 'planet.ofCap', {
          cap: num(locale, c?.populationCap ?? planet.population),
        }),
        alert: false,
      },
      {
        label: t(locale, 'planet.jobs'),
        value: num(locale, c?.jobs ?? 0),
        note: t(locale, 'planet.staffed', { pct: num(locale, (c?.staffing ?? 1) * 100) }),
        alert: (c?.staffing ?? 1) < 0.999,
      },
      {
        label: t(locale, 'planet.energy'),
        value: `${num(locale, (c?.solarEnergy ?? 0) + (c?.reactorEnergy ?? 0), 1)} / ${num(locale, c?.energyDemand ?? 0, 1)}`,
        note: t(locale, 'planet.energyNote', {
          solar: num(locale, c?.solarEnergy ?? 0, 1),
          reactor: num(locale, c?.reactorEnergy ?? 0, 1),
        }),
        alert: (c?.energyRatio ?? 1) < 0.999,
      },
      {
        label: t(locale, 'planet.food'),
        value: `${num(locale, (c?.fed.food ?? 1) * 100)}%`,
        note:
          planet.hungryCycles > 0
            ? t(locale, 'planet.hungry', { n: planet.hungryCycles })
            : t(locale, 'planet.fedNote'),
        alert: (c?.fed.food ?? 1) < 0.999,
      },
      {
        label: t(locale, 'planet.satisfaction'),
        value: `${num(locale, (c?.satisfaction ?? 1) * 100)}%`,
        note: t(locale, 'planet.output', { x: num(locale, c?.outputMultiplier ?? 1, 2) }),
        alert: (c?.satisfaction ?? 1) < 0.5,
      },
      {
        label: t(locale, 'planet.science'),
        value: `+${num(locale, c?.science ?? 0, 1)}`,
        note: t(locale, 'planet.scienceStock', { n: num(locale, planet.science) }),
        alert: false,
      },
    ] as Stat[]
  ).map((s, i) =>
    // Before the first cycle closes nothing is measured yet: say so once, not zeros.
    c || i === 0
      ? s
      : { ...s, value: '—', note: i === 1 ? t(locale, 'planet.firstCycle') : '', alert: false },
  );

  const buildings: BuildingRow[] = planet.buildings.map((b, index) => {
    const blocker = upgradeBlocker(planet, index);
    const next = b.level + 1;
    return {
      index,
      id: b.id,
      name: t(locale, `building.${b.id}.name`),
      layer: b.layer,
      level: b.level,
      maxLevel: ECONOMY.maxBuildingLevel,
      building: b.construction
        ? {
            level: b.construction.targetLevel,
            left: clock(Math.max(0, b.construction.readyTick - state.tick)),
            value: Math.max(
              0,
              Math.min(
                1,
                1 -
                  (b.construction.readyTick - state.tick) /
                    (BUILDINGS[b.id].buildCycles * BALANCE.cycleTicks),
              ),
            ),
          }
        : null,
      does: buildingDoes(locale, b.id, player),
      upgrade:
        b.level >= ECONOMY.maxBuildingLevel || b.construction
          ? null
          : offer(locale, player, b.id, next, { type: 'upgrade', playerId: me, index }, blocker),
    };
  });

  const options: BuildOption[] = [];
  for (const id of BUILDING_IDS) {
    const def = BUILDINGS[id];
    // A unique building already standing is upgraded from its slot, not built again.
    if (def.unique && planet.buildings.some((b) => b.id === id)) continue;
    for (const layer of def.layers) {
      const tech = def.requiresTech;
      const locked = tech !== null && !player.techs.includes(tech);
      const blocker = locked ? 'requires technology' : buildBlocker(planet, id, layer, mods);
      options.push({
        id,
        layer,
        name: t(locale, `building.${id}.name`),
        does: buildingDoes(locale, id, player),
        offer: offer(
          locale,
          player,
          id,
          1,
          { type: 'build', playerId: me, building: id, layer },
          blocker,
          layer,
        ),
        locked,
      });
    }
  }
  // Ready first, then those short of money, then the rest; locked last.
  const rank = (o: BuildOption) =>
    o.locked ? 3 : o.offer.command ? 0 : o.offer.code === 'no free slot' ? 2 : 1;
  options.sort((a, b) => rank(a) - rank(b));

  return {
    title: home?.homeOf ? t(locale, 'planet.capital') : '',
    subtitle: t(locale, 'planet.where', {
      kind: t(locale, `planet.${home?.kind ?? 'terran'}.name`),
      orbit,
      cheap: t(locale, `resource.${FACTIONS[player.faction].cheapGood}`).toLowerCase(),
    }),
    stats,
    slots: {
      surface: { used: slotsUsed(planet, 'surface'), total: slotCount(planet, 'surface', mods) },
      orbit: { used: slotsUsed(planet, 'orbit'), total: slotCount(planet, 'orbit', mods) },
    },
    buildings,
    options,
    cycle: c
      ? TRADE_GOOD_IDS.map((good) => ({
          good,
          produced: c.produced[good],
          consumed: c.consumed[good],
          net: c.produced[good] - c.consumed[good],
        }))
      : [],
  };
}
